import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { fetchCodeEditContext, sqlQuery } from '@/shared/helpers/api';
import { http } from '@/shared/httpWrapper.js';
import { parseError } from '@/shared/helpers/errorMessageParser';
import type { CodeEditContextResponse } from '@/shared/helpers/api/types/codeEditContextResponse';
import {
    cloneEdgeMap,
    cloneNodeMap,
    createLibraryColor,
    createLibraryMutedColor,
    edgeKey,
    getRowValue,
    mergeNode,
    nodeKey,
    normalizeNodeFromRow,
    parseCodeRefTarget,
    quoteSqlIdentifier,
    toNumber,
    toStringOrNull
} from './codeNetworkStore.helpers';
import type {
    CodeNetworkAttributeCacheEntry,
    CodeNetworkAttributeRow,
    CodeNetworkEdge,
    CodeNetworkNode,
    CodeRefAttribute,
    GraphSnapshot,
    LibraryDisplayOption,
    SqlRow
} from './codeNetworkStore.types';

const MAX_EXPAND_UNDO_LEVELS = 5;
const ATTRIBUTE_CACHE_TTL_MS = 5 * 60 * 1000;

export const useCodeNetworkStore = defineStore('codeNetwork', () => {
    const nodesByKey = ref<Record<string, CodeNetworkNode>>({});
    const edgesByKey = ref<Record<string, CodeNetworkEdge>>({});
    const libraryColorByName = ref<Record<string, string>>({});
    const libraryVisibility = ref<Record<string, boolean>>({});
    const selectedNodeKey = ref<string | null>(null);
    const expandedNodeKeys = ref<Record<string, true>>({});
    const expandingNodeKeys = ref<Record<string, true>>({});
    const showInvalid = ref<boolean>(false);
    const errorMessage = ref<string | null>(null);
    const loadingCounter = ref<number>(0);
    const expandUndoStack = ref<GraphSnapshot[]>([]);
    const attributeCacheByNodeKey = ref<Record<string, CodeNetworkAttributeCacheEntry>>({});
    const attributesLoadingByNodeKey = ref<Record<string, true>>({});
    const attributeRequestsByNodeKey = new Map<string, Promise<void>>();

    const loading = computed<boolean>(() => loadingCounter.value > 0);

    const visibleNodes = computed<CodeNetworkNode[]>(() => {
        return Object.values(nodesByKey.value)
            .filter(node => libraryVisibility.value[node.library] !== false)
            .filter(node => showInvalid.value || node.isValid);
    });

    const libraryDisplayOptions = computed<LibraryDisplayOption[]>(() => {
        const countsByLibrary: Record<string, number> = {};

        Object.values(nodesByKey.value).forEach(node => {
            countsByLibrary[node.library] = (countsByLibrary[node.library] || 0) + 1;
        });

        const libraries = Object.keys(countsByLibrary).sort((a, b) => a.localeCompare(b));
        return libraries.map(library => ({
            library,
            color: libraryColorByName.value[library] || createLibraryColor(library),
            visible: libraryVisibility.value[library] !== false,
            totalNodes: countsByLibrary[library]
        }));
    });

    const visibleNodeKeySet = computed<Set<string>>(() => {
        return new Set(visibleNodes.value.map(node => node.key));
    });

    const graphNodes = computed(() => {
        return visibleNodes.value.map(node => ({
            id: node.key,
            label: `${node.name}\n${node.library}`,
            color: {
                background: node.isValid
                    ? libraryColorByName.value[node.library]
                    : createLibraryMutedColor(node.library),
                border: libraryColorByName.value[node.library],
                highlight: {
                    background: libraryColorByName.value[node.library],
                    border: libraryColorByName.value[node.library]
                }
            },
            font: { color: '#222222' }
        }));
    });

    const graphEdges = computed(() => {
        return Object.values(edgesByKey.value)
            .filter(edge => visibleNodeKeySet.value.has(edge.from) && visibleNodeKeySet.value.has(edge.to))
            .map(edge => ({
                from: edge.from,
                to: edge.to,
                arrows: 'to',
                title: edge.attribute
            }));
    });

    const selectedNode = computed<CodeNetworkNode | null>(() => {
        if (!selectedNodeKey.value) {
            return null;
        }

        return nodesByKey.value[selectedNodeKey.value] ?? null;
    });

    const selectedOutgoing = computed(() => {
        if (!selectedNode.value) {
            return [];
        }

        const selectedKey = selectedNode.value.key;
        return Object.values(edgesByKey.value)
            .filter(edge => edge.from === selectedKey && visibleNodeKeySet.value.has(edge.to))
            .map(edge => ({
                attribute: edge.attribute,
                node: nodesByKey.value[edge.to]
            }))
            .filter(row => !!row.node)
            .sort((a, b) => `${a.node.library}:${a.node.name}`.localeCompare(`${b.node.library}:${b.node.name}`));
    });

    const selectedIncoming = computed(() => {
        if (!selectedNode.value) {
            return [];
        }

        const selectedKey = selectedNode.value.key;
        return Object.values(edgesByKey.value)
            .filter(edge => edge.to === selectedKey && visibleNodeKeySet.value.has(edge.from))
            .map(edge => ({
                attribute: edge.attribute,
                node: nodesByKey.value[edge.from]
            }))
            .filter(row => !!row.node)
            .sort((a, b) => `${a.node.library}:${a.node.name}`.localeCompare(`${b.node.library}:${b.node.name}`));
    });

    const canExpandSelectedNode = computed<boolean>(() => {
        if (!selectedNode.value) {
            return false;
        }

        return !expandedNodeKeys.value[selectedNode.value.key];
    });

    const selectedNodeExpanding = computed<boolean>(() => {
        if (!selectedNode.value) {
            return false;
        }

        return !!expandingNodeKeys.value[selectedNode.value.key];
    });

    const selectedNodeAttributeRows = computed<CodeNetworkAttributeRow[]>(() => {
        if (!selectedNodeKey.value) {
            return [];
        }

        return attributeCacheByNodeKey.value[selectedNodeKey.value]?.rows ?? [];
    });

    const selectedNodeAttributesLoading = computed<boolean>(() => {
        if (!selectedNodeKey.value) {
            return false;
        }

        return !!attributesLoadingByNodeKey.value[selectedNodeKey.value];
    });

    const selectedNodeAttributesLoaded = computed<boolean>(() => {
        if (!selectedNodeKey.value) {
            return false;
        }

        return Object.prototype.hasOwnProperty.call(attributeCacheByNodeKey.value, selectedNodeKey.value);
    });

    const nodeCount = computed<number>(() => visibleNodes.value.length);
    const edgeCount = computed<number>(() => graphEdges.value.length);
    const canUndo = computed<boolean>(() => expandUndoStack.value.length > 0);

    function toAttributeDisplayValue(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        return String(value);
    }

    function mapAttributeRows(context: CodeEditContextResponse): CodeNetworkAttributeRow[] {
        const attributesByName = new Map(
            context.code.attributes.map(attribute => [attribute.definitionName, attribute])
        );

        const orderedRows = [...context.library.attributeDefinitions]
            .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
            .map(definition => {
                const attribute = attributesByName.get(definition.name);
                const hasDisplayValue
                    = attribute
                        && attribute.displayValue !== null
                        && attribute.displayValue !== undefined
                        && attribute.displayValue !== '';

                return {
                    definitionName: definition.name,
                    displayName: definition.displayAs || definition.name,
                    value: hasDisplayValue
                        ? attribute?.displayValue ?? ''
                        : toAttributeDisplayValue(attribute?.value),
                    attributeType: definition.attributeType,
                    sequenceNumber: definition.sequenceNumber
                };
            });

        const knownDefinitionNames = new Set(
            context.library.attributeDefinitions.map(definition => definition.name)
        );

        const extraRows = context.code.attributes
            .filter(attribute => !knownDefinitionNames.has(attribute.definitionName))
            .map(attribute => ({
                definitionName: attribute.definitionName,
                displayName: attribute.definitionName,
                value: attribute.displayValue || toAttributeDisplayValue(attribute.value),
                attributeType: attribute.attributeType,
                sequenceNumber: Number.MAX_SAFE_INTEGER
            }));

        return [...orderedRows, ...extraRows];
    }

    function createSnapshot(): GraphSnapshot {
        return {
            nodesByKey: cloneNodeMap(nodesByKey.value),
            edgesByKey: cloneEdgeMap(edgesByKey.value),
            libraryColorByName: { ...libraryColorByName.value },
            libraryVisibility: { ...libraryVisibility.value },
            selectedNodeKey: selectedNodeKey.value,
            expandedNodeKeys: { ...expandedNodeKeys.value }
        };
    }

    function pushUndoSnapshot(snapshot: GraphSnapshot): void {
        if (expandUndoStack.value.length >= MAX_EXPAND_UNDO_LEVELS) {
            expandUndoStack.value.shift();
        }

        expandUndoStack.value.push(snapshot);
    }

    function restoreSnapshot(snapshot: GraphSnapshot): void {
        nodesByKey.value = cloneNodeMap(snapshot.nodesByKey);
        edgesByKey.value = cloneEdgeMap(snapshot.edgesByKey);
        libraryColorByName.value = { ...snapshot.libraryColorByName };
        libraryVisibility.value = { ...snapshot.libraryVisibility };
        selectedNodeKey.value = snapshot.selectedNodeKey;
        expandedNodeKeys.value = { ...snapshot.expandedNodeKeys };
        expandingNodeKeys.value = {};

        ensureSelectedNodeAttributesLoaded();
    }

    function undoLastExpand(): void {
        const snapshot = expandUndoStack.value.pop();
        if (!snapshot) {
            return;
        }

        restoreSnapshot(snapshot);
    }

    function incrementLoading() {
        loadingCounter.value += 1;
    }

    function decrementLoading() {
        loadingCounter.value = Math.max(loadingCounter.value - 1, 0);
    }

    function addOrUpdateNode(node: CodeNetworkNode) {
        if (!(node.library in libraryColorByName.value)) {
            libraryColorByName.value[node.library] = createLibraryColor(node.library);
        }

        if (!(node.library in libraryVisibility.value)) {
            libraryVisibility.value[node.library] = true;
        }

        nodesByKey.value[node.key] = mergeNode(node, nodesByKey.value[node.key]);
    }

    function setLibraryVisible(library: string, visible: boolean) {
        if (!(library in libraryVisibility.value)) {
            return;
        }

        libraryVisibility.value[library] = visible;
    }

    function addEdge(from: string, to: string, attribute: string) {
        const normalizedAttribute = attribute || 'CodeRef';
        const key = edgeKey(from, to, normalizedAttribute);
        edgesByKey.value[key] = {
            key,
            from,
            to,
            attribute: normalizedAttribute
        };
    }

    async function runSql(query: string, argumentsMap?: Record<string, string>): Promise<SqlRow[]> {
        const response = await sqlQuery(http, {
            query,
            arguments: argumentsMap
        });
        return response.data;
    }

    function clearAttributeCache() {
        attributeCacheByNodeKey.value = {};
        attributesLoadingByNodeKey.value = {};
        attributeRequestsByNodeKey.clear();
    }

    async function loadNodeAttributes(nodeKeyToLoad: string, forceReload = false): Promise<void> {
        const node = nodesByKey.value[nodeKeyToLoad];
        if (!node) {
            return;
        }

        const cachedEntry = attributeCacheByNodeKey.value[nodeKeyToLoad];
        const isCacheFresh = !!cachedEntry && (Date.now() - cachedEntry.fetchedAt < ATTRIBUTE_CACHE_TTL_MS);
        if (!forceReload && isCacheFresh) {
            return;
        }

        const inFlightRequest = attributeRequestsByNodeKey.get(nodeKeyToLoad);
        if (inFlightRequest) {
            await inFlightRequest;
            return;
        }

        const request = (async () => {
            attributesLoadingByNodeKey.value[nodeKeyToLoad] = true;
            incrementLoading();

            try {
                errorMessage.value = null;
                const response = await fetchCodeEditContext(http, { codeId: node.id });
                attributeCacheByNodeKey.value[nodeKeyToLoad] = {
                    rows: mapAttributeRows(response.data),
                    fetchedAt: Date.now()
                };
            } catch (error) {
                errorMessage.value = parseError(error as object);
            } finally {
                delete attributesLoadingByNodeKey.value[nodeKeyToLoad];
                attributeRequestsByNodeKey.delete(nodeKeyToLoad);
                decrementLoading();
            }
        })();

        attributeRequestsByNodeKey.set(nodeKeyToLoad, request);
        await request;
    }

    function ensureSelectedNodeAttributesLoaded() {
        if (!selectedNodeKey.value) {
            return;
        }

        void loadNodeAttributes(selectedNodeKey.value);
    }

    function resetGraph() {
        nodesByKey.value = {};
        edgesByKey.value = {};
        libraryColorByName.value = {};
        libraryVisibility.value = {};
        selectedNodeKey.value = null;
        expandedNodeKeys.value = {};
        expandingNodeKeys.value = {};
        expandUndoStack.value = [];
        clearAttributeCache();
        errorMessage.value = null;
        loadingCounter.value = 0;
    }

    function selectNode(key: string | null) {
        if (!key || !nodesByKey.value[key]) {
            selectedNodeKey.value = null;
            return;
        }

        selectedNodeKey.value = key;
        ensureSelectedNodeAttributesLoaded();
    }

    async function getStartNode(dbId: number): Promise<CodeNetworkNode | null> {
        const rows = await runSql(
            `SELECT TOP 1 [Library], [Id], [Name], [Description], [IsValid]
             FROM [_LibraryItem]
             WHERE [Id] = @codeId`,
            { codeId: String(dbId) }
        );

        if (rows.length === 0) {
            return null;
        }

        return normalizeNodeFromRow(rows[0]);
    }

    async function getNodesFromLibraryItems(library: string, ids: number[]): Promise<CodeNetworkNode[]> {
        if (ids.length === 0) {
            return [];
        }

        const placeholders = ids.map((_, idx) => `@id${idx}`).join(', ');
        const args: Record<string, string> = {
            library
        };

        ids.forEach((id, idx) => {
            args[`id${idx}`] = String(id);
        });

        const rows = await runSql(
            `SELECT [Library], [Id], [Name], [Description], [IsValid]
             FROM [_LibraryItem]
             WHERE [Library] = @library
               AND [Id] IN (${placeholders})`,
            args
        );

        return rows
            .map(row => normalizeNodeFromRow(row))
            .filter((row): row is CodeNetworkNode => !!row);
    }

    async function getOutgoingAttributes(library: string): Promise<CodeRefAttribute[]> {
        const rows = await runSql(
            `SELECT DISTINCT [Library], [AttributeName], [DataType]
             FROM [_Attribute]
             WHERE [Library] = @library
               AND [DataType] LIKE 'CodeRef:%'`,
            { library }
        );

        return rows
            .map(row => {
                const sourceLibrary = toStringOrNull(getRowValue(row, 'Library'));
                const attributeName = toStringOrNull(getRowValue(row, 'AttributeName'));
                const targetLibrary = parseCodeRefTarget(toStringOrNull(getRowValue(row, 'DataType')));
                if (!sourceLibrary || !attributeName || !targetLibrary) {
                    return null;
                }

                return {
                    sourceLibrary,
                    attributeName,
                    targetLibrary
                };
            })
            .filter((row): row is CodeRefAttribute => !!row);
    }

    async function getIncomingAttributes(targetLibrary: string): Promise<CodeRefAttribute[]> {
        const rows = await runSql(
            `SELECT DISTINCT [Library], [AttributeName]
             FROM [_Attribute]
             WHERE [DataType] = @dataType`,
            { dataType: `CodeRef:${targetLibrary}` }
        );

        return rows
            .map(row => {
                const sourceLibrary = toStringOrNull(getRowValue(row, 'Library'));
                const attributeName = toStringOrNull(getRowValue(row, 'AttributeName'));
                if (!sourceLibrary || !attributeName) {
                    return null;
                }

                return {
                    sourceLibrary,
                    attributeName,
                    targetLibrary
                };
            })
            .filter((row): row is CodeRefAttribute => !!row);
    }

    async function loadOutgoingReferences(sourceNode: CodeNetworkNode): Promise<void> {
        const attributes = await getOutgoingAttributes(sourceNode.library);
        if (attributes.length === 0) {
            return;
        }

        const sourceTable = quoteSqlIdentifier(sourceNode.library);
        const sourceRows = await runSql(
            `SELECT TOP 1 *
             FROM ${sourceTable}
             WHERE [Id] = @sourceId`,
            { sourceId: String(sourceNode.id) }
        );

        if (sourceRows.length === 0) {
            return;
        }

        const sourceRow = sourceRows[0];
        const targetIdsByLibrary = new Map<string, Set<number>>();
        const references: Array<{ targetLibrary: string; targetId: number; attributeName: string }> = [];

        for (const attribute of attributes) {
            const idColumn = `${attribute.attributeName}_ID`;
            const rawValue = getRowValue(sourceRow, idColumn) ?? getRowValue(sourceRow, attribute.attributeName);
            const targetId = toNumber(rawValue);

            if (targetId === null) {
                continue;
            }

            if (!targetIdsByLibrary.has(attribute.targetLibrary)) {
                targetIdsByLibrary.set(attribute.targetLibrary, new Set<number>());
            }

            targetIdsByLibrary.get(attribute.targetLibrary)?.add(targetId);
            references.push({
                targetLibrary: attribute.targetLibrary,
                targetId,
                attributeName: attribute.attributeName
            });
        }

        for (const [targetLibrary, idSet] of targetIdsByLibrary.entries()) {
            const targets = await getNodesFromLibraryItems(targetLibrary, Array.from(idSet));
            targets.forEach(addOrUpdateNode);

            for (const id of idSet) {
                const key = nodeKey(targetLibrary, id);
                if (!nodesByKey.value[key]) {
                    addOrUpdateNode({
                        key,
                        library: targetLibrary,
                        id,
                        name: `Code ${id}`,
                        description: '',
                        isValid: true,
                        identity: null,
                        scope: null,
                        iri: null
                    });
                }
            }
        }

        const fromKey = sourceNode.key;
        references.forEach(reference => {
            addEdge(fromKey, nodeKey(reference.targetLibrary, reference.targetId), reference.attributeName);
        });
    }

    async function queryIncomingRows(sourceLibrary: string, attributeName: string, targetId: number): Promise<SqlRow[]> {
        const sourceTable = quoteSqlIdentifier(sourceLibrary);
        const refIdColumn = quoteSqlIdentifier(`${attributeName}_ID`);

        return runSql(
            `SELECT *
             FROM ${sourceTable}
             WHERE ${refIdColumn} = @targetId`,
            { targetId: String(targetId) }
        );
    }

    async function loadIncomingReferences(targetNode: CodeNetworkNode): Promise<void> {
        const incomingAttributes = await getIncomingAttributes(targetNode.library);
        if (incomingAttributes.length === 0) {
            return;
        }

        for (const incomingAttribute of incomingAttributes) {
            const sourceRows = await queryIncomingRows(
                incomingAttribute.sourceLibrary,
                incomingAttribute.attributeName,
                targetNode.id
            );

            sourceRows.forEach(row => {
                const sourceNode = normalizeNodeFromRow(row, incomingAttribute.sourceLibrary);
                if (!sourceNode) {
                    return;
                }

                addOrUpdateNode(sourceNode);
                addEdge(sourceNode.key, targetNode.key, incomingAttribute.attributeName);
            });
        }
    }

    async function expandNode(nodeKeyToExpand: string, pushUndoState = true): Promise<void> {
        const node = nodesByKey.value[nodeKeyToExpand];
        if (!node) {
            return;
        }

        if (expandedNodeKeys.value[nodeKeyToExpand] || expandingNodeKeys.value[nodeKeyToExpand]) {
            return;
        }

        if (pushUndoState) {
            pushUndoSnapshot(createSnapshot());
        }

        expandingNodeKeys.value[nodeKeyToExpand] = true;
        incrementLoading();

        try {
            errorMessage.value = null;
            await Promise.all([
                loadOutgoingReferences(node),
                loadIncomingReferences(node)
            ]);
            expandedNodeKeys.value[nodeKeyToExpand] = true;
        } catch (error) {
            errorMessage.value = parseError(error as object);
        } finally {
            delete expandingNodeKeys.value[nodeKeyToExpand];
            decrementLoading();
        }
    }

    async function initializeByDbId(dbId: number): Promise<void> {
        resetGraph();
        incrementLoading();

        try {
            const startNode = await getStartNode(dbId);
            if (!startNode) {
                throw new Error(`No code was found for database ID ${dbId}`);
            }

            // Default behavior for each entrypoint load:
            // hide invalid codes unless the entrypoint itself is invalid.
            showInvalid.value = !startNode.isValid;

            addOrUpdateNode(startNode);
            selectNode(startNode.key);
            await expandNode(startNode.key, false);
        } catch (error) {
            errorMessage.value = parseError(error as object);
        } finally {
            decrementLoading();
        }
    }

    return {
        showInvalid,
        errorMessage,
        loading,
        graphNodes,
        graphEdges,
        libraryDisplayOptions,
        selectedNode,
        selectedOutgoing,
        selectedIncoming,
        selectedNodeExpanding,
        selectedNodeAttributeRows,
        selectedNodeAttributesLoading,
        selectedNodeAttributesLoaded,
        canExpandSelectedNode,
        nodeCount,
        edgeCount,
        canUndo,

        nodesByKey,
        edgesByKey,
        libraryColorByName,
        libraryVisibility,
        selectedNodeKey,
        expandedNodeKeys,
        expandingNodeKeys,
        loadingCounter,
        expandUndoStack,
        attributeCacheByNodeKey,
        attributesLoadingByNodeKey,

        selectNode,
        setLibraryVisible,
        initializeByDbId,
        expandNode,
        undoLastExpand
    };
});
